<template>
  <div class="s-uploader" @dragover.prevent @drop.prevent @submit.prevent>
    <p v-if="label" class="label">{{ label }}</p>

    <div v-if="files.length" class="uploader-file-viewer">
      <div v-if="isLeftArrowVisible" class="uploader-file-viewer__arrow uploader-file-viewer__arrow--left">
        <div class="arrow" @click.stop="onLeftArrowClick">
          <SIcon name="arrow_big_left" />
        </div>
      </div>

      <div class="uploader-file-viewer__files">
        <Splide
          v-if="displayMode === DisplayMode.IMAGE_ONLY"
          ref="carousel"
          :options="splideOptions"
          class="uploader-file-viewer__carousel"
          @splide:moved="refreshSpliderAfterMove"
          @splide:click="refreshSpliderAfterMove"
        >
          <SplideSlide v-if="isDesktopPreview" class="carousel-slide__btn" style="width: 39px; height: 80px">
            <div class="carousel-slide__btn--add" @click="onChooseFileClick">
              <SIcon name="clip_attachment" color="white" size="tn" />
            </div>
          </SplideSlide>

          <template v-for="(file, index) in files" :key="`${file.name}-${index}`">
            <SplideSlide style="width: 80px">
              <div class="uploader-file-viewer__files--file">
                <FilePreview
                  v-if="getFileState(file) === 'loaded'"
                  :file="file"
                  :sizes="{
                    height: imageHeight,
                    width: imageWidth
                  }"
                  @click="checkFileAction(file, getFileIndex(file))"
                />
                <FileErrorPlaceholder v-else-if="getFileState(file) === 'error'" />
                <div class="file-trash">
                  <SIcon class="trash-icon" name="trash" @click="onRemoveIconClick(index)" />
                </div>
              </div>
            </SplideSlide>
          </template>
        </Splide>
        <SScroll v-else size="S" class="uploader-file-viewer__scroll">
          <div v-for="(file, index) in files" :key="`${file.name}-${index}`" class="file">
            <div class="file__content">
              <FilePreview
                v-if="getFileState(file) === 'loaded'"
                :file="file"
                :sizes="{
                  height: imageHeight,
                  width: imageWidth
                }"
                @click="checkFileAction(file, getFileIndex(file))"
              />
              <FileErrorPlaceholder v-else-if="getFileState(file) === 'error'" />
              <div class="file__description">
                <div class="file__description-name" @mouseenter="onNameMouseEnter(index)" @mouseleave="onNameMouseLeave(index)">
                  <STooltip ref="fileNameTooltipInstances" :append-to-body="true" has-external-control>
                    <template #text>
                      <span ref="fileNameTextElements" class="file__description-name-text">{{ file.name }}</span>
                    </template>
                    <template #body>
                      {{ file.name }}
                    </template>
                  </STooltip>
                </div>
                <div class="file__description-size">{{ formatFileSize(file.size) }}</div>
              </div>
            </div>
            <div class="file__actions">
              <button class="file__action-btn" @click="onDownloadClick(file)">
                <SIcon class="file-action-icon" name="download" />
              </button>
              <button class="file__action-btn" @click="onRemoveIconClick(index)">
                <SIcon class="file-action-icon" name="trash" />
              </button>
            </div>
          </div>
        </SScroll>
      </div>

      <div v-if="isRightArrowVisible" class="uploader-file-viewer__arrow uploader-file-viewer__arrow--right">
        <div class="arrow" @click.stop="onRightArrowClick">
          <SIcon name="arrow_big_right" />
        </div>
      </div>
    </div>

    <div
      v-if="!isDesktopPreview || (isDesktopPreview && files.length <= 0)"
      class="drag-n-drop-area"
      @drop.prevent="onDrop"
    >
      <div class="drag-n-drop-area--header">
        <div class="cloud-icon">
          <SIcon name="cloud-storage-upload-2" />
        </div>
      </div>

      <div class="drag-n-drop-area--errors">
        <slot name="dragNdropErrors"></slot>
      </div>
      <div class="drag-n-drop-area--body">
        <div class="title" :class="dropAreaTitleClass">{{ dropAreaTitle }}</div>
        <div class="description" :class="descriptionClass">{{ description }}</div>
      </div>
      <SButton
        variant="secondary"
        size="small"
        :disabled="disabled"
        @click="onChooseFileClick"
      >
        {{ dropAreaButtonText }}
      </SButton>
    </div>

    <FileViewer
      v-if="isUploaderModalDialogVisible"
      :label="viewerTitle"
      :files="filteredFiles"
      removable
      :file-index="fileIndex"
      :image-formats="imageFormats"
      @hide-dialog="onHideModalDialog"
      @remove-file="onRemoveIconClick"
      @file-index-changed="onFileIndexChange"
    />

    <input
      :id="id"
      ref="fileInput"
      type="file"
      :name="name"
      :accept="accept"
      :multiple="multiple"
      class="file-input"
      @change="onChange"
    />
    <FilePlugSprite />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted, onUpdated, onBeforeUnmount } from 'vue'
import type { PropType } from 'vue'

import { Splide, SplideSlide } from '@splidejs/vue-splide'
import type { Splide as SplideInstance } from '@splidejs/splide'
import '@splidejs/vue-splide/css'

import SScroll from '@components/scroll/Scroll.vue'
import SButton from '@components/button/Button.vue'
import SIcon from '@components/icon/Icon.vue'
import STooltip from '@components/tooltip/Tooltip.vue'
import FileViewer from '@components/file/FileViewer.vue'
import FilePreview from '@components/file/FilePreview.vue'
import FilePlugSprite from '@components/file/FilePlugSprite.vue'
import FileErrorPlaceholder from '@components/file/FileErrorPlaceholder.vue'
import { formatFileSize, filesToFileList } from './helper'
import { isRemoteFile, isLocalFile } from '@/types/uploader'
import { DisplayMode, UploaderFile, FileState } from '@/types/uploader'
import type { SplideComponentInstance } from './types'

const files = defineModel('files', { type: Array<UploaderFile>, required: true })
const fileNameTextElements = ref<HTMLElement[]>([])
const fileNameTooltipInstances = ref<InstanceType<typeof STooltip>[]>([])

const props = defineProps({
  displayMode: {
    type: String as PropType<DisplayMode>,
    default: DisplayMode.IMAGE_ONLY
  },
  imageWidth: {
    type: Number,
    default: 80
  },
  imageHeight: {
    type: Number,
    default: 80
  },
  description: {
    type: String,
    default: ''
  },
  descriptionClass: {
    type: String,
    default: ''
  },
  viewerTitle: {
    type: String,
    default: 'Просмотр файла'
  },
  label: {
    type: String,
    default: ''
  },
  name: {
    type: String,
    default: ''
  },
  accept: {
    type: String,
    default: '.jpeg,.jpg,.png,.pdf'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  dropAreaTitle: {
    type: String,
    default: 'Перетащите файл или выберите на компьютере',
  },
  dropAreaTitleClass: {
    type: String,
    default: ''
  },
  dropAreaButtonText: {
    type: String,
    default: "Выбрать файл",
  },
  getFileState: {
    type: Function as PropType<(file: UploaderFile) => FileState>,
    default: () => 'loaded'
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits<{
  'file-click': [{ file: UploaderFile; index: number }]
  'file-removed': [index: number]
}>()

const id = `s-uploader_${Date.now().toString()}`

const imageFormats = ['apng', 'gif', 'ico', 'cur', 'jpg', 'jpeg', 'pjpeg', 'pjp', 'png', 'svg', 'webp', 'bmp']

const splideOptions = {
  type: 'slide',
  width: '100%',
  autoWidth: true,
  perMove: 1,
  pagination: false,
  arrows: false,
  drag: false,
  breakpoints: {
    744: {
      drag: true
    }
  },
  rewind: true,
  gap: '8px'
}

const fileIndex = ref(0)
const isDesktopPreview = ref(true)
const isLeftArrowVisible = ref(false)
const isRightArrowVisible = ref(false)
const isUploaderModalDialogVisible = ref(false)

const fileInput = ref<HTMLInputElement | null>(null)
const carousel = ref<SplideComponentInstance | null>(null)

const filteredFiles = computed<UploaderFile[]>(() => {
  return files.value
})

watch(files, (newFiles) => {
  if (!fileInput.value) {
    return
  }

  const fileItems = newFiles.filter((file) => isLocalFile(file)) as File[]

  if (fileItems.length) {
    fileInput.value.files = filesToFileList(fileItems)
  } else {
    fileInput.value.value = ''
  }
})

const getFileExtension = (filename: string) => {
  return filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2).toLowerCase()
}

const checkFileAction = (file: UploaderFile, index: number) => {
  if (imageFormats.includes(getFileExtension(file.name)) || getFileExtension(file.name) === 'pdf') {
    onFileClick(file, index)
  } else {
    onDownloadClick(file)
  }
}

const getFileIndex = (file: UploaderFile): number => {
  return filteredFiles.value.findIndex(({ name }) => name === file.name)
}

const onChange = () => {
  files.value = props.multiple ? [...files.value, ...fileInput.value!.files!] : [...fileInput.value!.files!]

  refreshAndRemount()
  setArrowsVisibility()
}

const onFileIndexChange = (index: number) => {
  fileIndex.value = index
}

const refreshAndRemount = () => {
  if (carousel.value) {
    carousel.value.splide.refresh()
    carousel.value.splide.destroy()
    carousel.value.splide.mount()
  }
}

const onChooseFileClick = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

const onDrop = (event: DragEvent) => {
  if (props.disabled) return
  if (!event.dataTransfer?.files) return
  if (!props.multiple && event.dataTransfer.files.length > 1) return

  const res = [
    ...Array.from(event.dataTransfer.files).filter((item) =>
      props.accept.split(',').includes(`.${getFileExtension(item.name)}`)
    )
  ]
  files.value = props.multiple ? [...files.value, ...res] : [res[0]]
}

const onHideModalDialog = () => {
  isUploaderModalDialogVisible.value = false
}

const refreshSpliderAfterMove = (splide: SplideInstance) => {
  splide.refresh()
}

const onRemoveIconClick = (index: number) => {
  const newFiles = [...files.value]
  newFiles.splice(index, 1)
  emit('file-removed', index)
  files.value = newFiles
  refreshAndRemount()
  if (!newFiles.length) isUploaderModalDialogVisible.value = false
}

const onFileClick = (file: UploaderFile, index: number) => {
  emit('file-click', { file, index })
  fileIndex.value = index
  isUploaderModalDialogVisible.value = true
}

const onDownloadClick = (file: UploaderFile) => {
  try {
    const anchor = document.createElement('a')
    anchor.href = isRemoteFile(file) ? file.link : URL.createObjectURL(file)
    anchor.download = file.name
    anchor.setAttribute('target', '_blank')
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } catch (error: unknown) {
    console.warn(error instanceof Error ? error.message : error)
  }
}

const onResize = () => {
  isDesktopPreview.value = window.innerWidth < 744
}

const setArrowsVisibility = () => {
  if (carousel.value) {
    isRightArrowVisible.value = carousel.value.splide.index + 7 < carousel.value.splide.length
    isLeftArrowVisible.value = carousel.value.splide.index > 0
  }
}

const onRightArrowClick = () => {
  if (carousel.value) {
    carousel.value.splide.go('>')
    carousel.value.splide.refresh()
    setArrowsVisibility()
  }
}

const onLeftArrowClick = () => {
  if (carousel.value) {
    carousel.value.splide.go('<')
    carousel.value.splide.refresh()
    setArrowsVisibility()
  }
}

const onNameMouseEnter = (idx: number) => {
  const textEl = fileNameTextElements.value[idx]
  if (textEl.scrollWidth === textEl.clientWidth) return
  const tooltip = fileNameTooltipInstances.value[idx]
  tooltip.showPopper()

}

const onNameMouseLeave = (idx: number) => {
  const textEl = fileNameTextElements.value[idx]
  if (textEl.scrollWidth === textEl.clientWidth) return
  const tooltip = fileNameTooltipInstances.value[idx]
  tooltip.hidePopper()
}

onMounted(() => {
  onResize()
  window.addEventListener('resize', onResize)
})

onUpdated(() => {
  setArrowsVisibility()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss" scoped>
@import '@/assets/styles/scss/mixins';

.s-uploader {
  display: flex;
  flex-direction: column;
  width: 100%;

  .drag-n-drop-area {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 1px var(--stroke-cards) dashed;
    border-radius: 8px;
    padding: 16px 32px 32px;
    background-color: var(--color-bg-primary);
    font-size: 14px;
    &--header {
      margin-bottom: 4px;
      .cloud-icon {
        display: flex;
        width: 100px;
        height: 100px;
        margin-bottom: 8px;
      }
      @media (max-width: 743px) {
        display: none;
      }
    }
    &--errors {
      font-size: 14px;
      line-height: 20px;
      color: var(--color-text-accent);
      margin-bottom: 8px;
    }
    &--body {
      margin-bottom: 20px;
      text-align: center;
      .title {
        font-size: 14px;
        line-height: 20px;
        color: var(--color-text-secondary);
        margin-bottom: 8px;
        @media (max-width: 743px) {
          font-size: 12px;
          line-height: 16px;
        }
      }
      .description {
        font-size: 12px;
        line-height: 16px;
        color: var(--color-text-secondary);
      }
    }
  }

  &-input {
    &__wrap {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
      cursor: pointer;

      &--icon {
        flex: 0 0 auto;
        margin-right: 12px;
      }
    }
  }

  .uploader-file-viewer {
    display: flex;
    position: relative;
    width: 100%;
    overflow: hidden;

    .file {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 12px;
      margin-bottom: 8px;
      width: 100%;

      &:last-child {
        margin-bottom: 0;
      }

      &__description {
        width: calc(100% - 60px);
        &-name {
          width: 100%;
          &-text {
            display: inline-block;
            width: 100%;
            color: var(--color-text-primary);
            font-size: 16px;
            line-height: 22px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
        &-size {
          color: var(--color-text-secondary);
          font-size: 14px;
          line-height: 20px;
        }
      }
      &__content {
        display: flex;
        flex-direction: row;
        gap: 12px;
        align-items: center;
        width: calc(100% - 60px);
      }
      &__actions {
        width: 60px;
        display: flex;
        gap: 12px;
      }

      &__action-btn {
        @include button-reset;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 24px;
        width: 24px;
        color: var(--color-icon-secondary);
        cursor: pointer;
        flex-shrink: 0;
        &:hover {
          color: var(--color-icon-primary);
        }
      }
    }

    &__arrow {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 100%;
      padding-bottom: 24px;

      &--left {
        width: 52px;
        margin-right: 12px;
        align-items: flex-start;
      }

      &--right {
        width: 52px;
        margin-left: 12px;
        align-items: flex-end;
      }

      .arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 44px;
        height: 44px;
        border-radius: 100px;
        background-color: var(--color-bg-secondary);
        border: 1px solid var(--stroke-cards);
        cursor: pointer;

        user-select: none;
        -moz-user-select: none;
        -webkit-user-select: none;
        -ms-user-select: none;
      }
    }

    &__files {
      width: 100%;
      position: relative;

      .splide__track .splide__list {

        .carousel-slide__btn {
          &--add {
            background: var(--color-icon-primary);
            border-radius: 4px;
            height: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }
        }
      }

      &::-webkit-scrollbar {
        display: none;
      }
      -ms-overflow-style: none;
      scrollbar-width: none;

      &--file {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 8px;

        .file-trash {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 24px;

          .trash-icon {
            display: none;
          }
        }

        &:hover {
          .file-trash {
            cursor: pointer;
            .trash-icon {
              display: block;
            }
          }

          .file-view {
            cursor: default;

            &--hover {
              display: flex;
              position: absolute;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 100%;

              .eye-icon {
                cursor: pointer;
              }
            }
          }

          @media (max-width: 743px) {
            .file-view {
              cursor: pointer;
              &--hover {
                display: none;
              }
            }
          }
        }

        @media (max-width: 743px) {
          .file-trash {
            display: none;
          }
        }
      }
    }

    &__carousel {
      margin-bottom: 12px;
    }

    &__scroll {
      margin-bottom: 24px;
      height: v-bind('`${Math.min(files.length, 4) * 56 - 8}px`');
    }

    @media (max-width: 743px) {
      &__arrow {
        display: none;
      }

      &__files {
        display: flex;
      }
    }
  }

  @media (max-width: 743px) {
    width: 100%;
  }

  .label {
    font-size: 18px;
    line-height: 1.3;
    margin-bottom: 18px;
  }

  .file-input {
    display: none;
  }
}

.files {
  &__list {
    &--row {
      display: flex;
      flex-direction: row;
      gap: 8px;
    }
  }
}
</style>
